import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useInvestorAuth } from '../../context/InvestorAuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const STATUS_LABELS = {
  submitted:   'Under Review',
  loi_pending: 'LOI Pending',
  loi_signed:  'LOI Signed',
  dd_open:     'DD Room Open',
  rejected:    'Not Selected',
  withdrawn:   'Withdrawn',
};

export default function MarketDashboardInvestor() {
  const { token, investorLogout, refreshInvestor } = useInvestorAuth();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pools, setPools] = useState([]);
  const [bids, setBids] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const meRes = await axios.get(`${API_URL}/api/market/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!mounted) return;
        const inv = meRes.data.investor;
        setMe(inv);
        refreshInvestor(inv);

        // Approved investors fetch pools + bids unconditionally — the NDA
        // gate was removed from the registration flow, so admin approval
        // is now the only access threshold.
        const [poolsRes, bidsRes] = await Promise.all([
          axios.get(`${API_URL}/api/market/pools`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { pools: [] } })),
          axios.get(`${API_URL}/api/market/bids`,  { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { bids: [] } })),
        ]);
        if (mounted) {
          setPools(poolsRes.data.pools || []);
          setBids(bidsRes.data.bids || []);
        }
      } catch (err) {
        console.error('[Market] /me failed:', err.response?.data?.error || err.message);
        if (err.response?.status === 401 || err.response?.status === 403) {
          investorLogout();
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token, investorLogout, refreshInvestor]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>;
  }

  const activePoolsCount = pools.length;
  const activeBids = bids.filter((b) => !['rejected', 'withdrawn'].includes(b.status));
  const mostRecentBid = bids[0]; // bids array is ordered by submitted_at DESC

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <header style={{
        backgroundColor: '#fff',
        borderBottom: '1px solid #e5e7eb',
        padding: '14px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <Link to="/market/dashboard" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
          <img src="/doorly-market-logo.png" alt="Doorly Market" style={{ height: '32px', width: 'auto', display: 'block' }} />
        </Link>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <Link to="/market/dashboard" style={{ ...navLink, color: '#3a6ad9' }}>Dashboard</Link>
          <Link to="/market/pools" style={navLink}>Pools</Link>
          <Link to="/market/bids" style={navLink}>My Bids</Link>
          <span style={{ fontSize: 13, color: '#6b7280' }}>
            {me?.firstName} {me?.lastName}{me?.firmName ? ` · ${me.firmName}` : ''}
          </span>
          <button
            onClick={investorLogout}
            style={{ backgroundColor: 'transparent', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer', color: '#374151' }}
          >
            Sign out
          </button>
        </nav>
      </header>

      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#111827', margin: '0 0 8px' }}>
          Welcome, {me?.firstName || 'Investor'}.
        </h1>
        <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 28 }}>
          Active mortgage pools and your bid activity.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 28 }}>
          <Card>
            <CardLabel>Active Pools</CardLabel>
            <CardValue>{activePoolsCount}</CardValue>
            <Link to="/market/pools" style={cardLink}>Browse Pools →</Link>
          </Card>
          <Card>
            <CardLabel>My Active Bids</CardLabel>
            <CardValue>{activeBids.length}</CardValue>
            {mostRecentBid ? (
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                Most recent: <strong style={{ color: '#374151' }}>{STATUS_LABELS[mostRecentBid.status] || mostRecentBid.status}</strong>
              </div>
            ) : null}
            <Link to="/market/bids" style={cardLink}>View Bids →</Link>
          </Card>
          <Card>
            <CardLabel>Account Status</CardLabel>
            <CardValue style={{ textTransform: 'capitalize' }}>{me?.status || '—'}</CardValue>
          </Card>
        </div>

        {activePoolsCount === 0 && (
          <div style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 28, textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
              Pool listings coming soon
            </div>
            <div style={{ fontSize: 14, color: '#6b7280' }}>
              Doorly's investor pools will be published soon. You'll be emailed when new pools go live.
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const Card = ({ children }) => (
  <div style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 20 }}>
    {children}
  </div>
);
const CardLabel = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>{children}</div>
);
const CardValue = ({ children, style }) => (
  <div style={{ fontSize: 26, fontWeight: 800, color: '#111827', marginBottom: 8, ...style }}>{children}</div>
);
const cardLink = { fontSize: 13, fontWeight: 700, color: '#3a6ad9', textDecoration: 'none' };
const navLink = { fontSize: 13, fontWeight: 600, color: '#374151', textDecoration: 'none' };

